import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, MessageCircle, Mail, Phone, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const vehicleTypes = [
  "Coche premium",
  "SUV",
  "Coche familiar",
  "Furgoneta",
  "Vehículo comercial",
  "Deportivo",
  "Otro",
];

const budgets = [
  "Menos de 20.000€",
  "20.000€ - 35.000€",
  "35.000€ - 60.000€",
  "60.000€ - 100.000€",
  "Más de 100.000€",
];

const WHATSAPP_NUMBER = "34633821110";
const WHATSAPP_MSG = encodeURIComponent("Hola Elecars, me gustaría recibir asesoramiento sobre la importación de un vehículo desde Alemania.");

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    vehicle_type: "",
    budget: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

 const submit = async (e) => {
  e.preventDefault();

  if (!form.name || !form.phone || !form.email || !form.vehicle_type || !form.budget) {
    toast.error("Por favor completa todos los campos obligatorios.");
    return;
  }

  setLoading(true);

  try {
    const formData = new FormData();

    formData.append("form-name", "contacto-elecars");
    formData.append("name", form.name);
    formData.append("phone", form.phone);
    formData.append("email", form.email);
    formData.append("vehicle_type", form.vehicle_type);
    formData.append("budget", form.budget);
    formData.append("message", form.message);

    await fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams(formData).toString(),
    });

    toast.success("Solicitud enviada. Nos pondremos en contacto contigo en menos de 24h.");

    setForm({
      name: "",
      phone: "",
      email: "",
      vehicle_type: "",
      budget: "",
      message: "",
    });
  } catch (err) {
    toast.error("Hubo un error. Inténtalo de nuevo o escríbenos por WhatsApp.");
  } finally {
    setLoading(false);
  }
};

  return (
    <section
      id="contacto"
      className="relative py-24 sm:py-32 bg-[#0A0A0A] border-t border-white/5"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <span className="text-xs uppercase tracking-[0.32em] text-[#D4AF37]">Contacto</span>
            <h2 className="font-heading mt-6 text-3xl sm:text-5xl font-light tracking-tight text-[#FFFFF0] leading-tight">
              Solicita tu asesoramiento gratuito.
            </h2>
            <p className="mt-6 text-base text-[#A3A3A3] leading-relaxed font-light">
              Cuéntanos qué vehículo buscas. Te llamamos en menos de 24 horas para
              entender mejor tu caso y proponerte las primeras opciones.
            </p>

            <div className="mt-12 space-y-4">
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MSG}`}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-link"
                className="group flex items-center gap-4 bg-white/[0.02] border border-white/10 p-5 hover:border-[#25D366]/50 hover:bg-white/[0.04] transition-all"
              >
                <div className="w-11 h-11 bg-[#25D366]/10 border border-[#25D366]/30 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-[#25D366]" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#737373]">WhatsApp directo</p>
                  <p className="font-heading text-base text-[#FFFFF0] mt-1">+34 633 821 110</p>
                </div>
                <ArrowRight className="w-4 h-4 text-[#A3A3A3] group-hover:text-[#25D366] group-hover:translate-x-1 transition-all" strokeWidth={1.5} />
              </a>

              <div className="flex items-center gap-4 bg-white/[0.02] border border-white/10 p-5">
                <div className="w-11 h-11 bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#737373]">Teléfono</p>
                  <p className="font-heading text-base text-[#FFFFF0] mt-1">+34 633 821 110</p>
                </div>
              </div>

              <div className="flex items-center gap-4 bg-white/[0.02] border border-white/10 p-5">
                <div className="w-11 h-11 bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#737373]">Email</p>
                  <p className="font-heading text-base text-[#FFFFF0] mt-1">info@elecars.es</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.form
            name="contacto-elecars"
            method="POST"
            data-netlify="true"
            netlify-honeypot="bot-field"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            onSubmit={submit}
            className="lg:col-span-7 bg-white/[0.02] border border-white/10 p-8 sm:p-10"
            data-testid="contact-form"
          >
        <input type="hidden" name="form-name" value="contacto-elecars" />

          <p style={{ display: "none" }}>
          <label>
            No rellenar: <input name="bot-field" />
            </label>
              </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Nombre *" testid="form-name">
                <input
                  type="text"
                  value={form.name}
                  onChange={handle("name")}
                  required
                  data-testid="form-name-input"
                  className="w-full bg-transparent border border-white/15 px-4 py-3.5 text-[#FFFFF0] placeholder-[#737373] text-sm focus:border-[#D4AF37]"
                  placeholder="Tu nombre completo"
                />
              </Field>

              <Field label="Teléfono *" testid="form-phone">
                <input
                  type="tel"
                  value={form.phone}
                  onChange={handle("phone")}
                  required
                  data-testid="form-phone-input"
                  className="w-full bg-transparent border border-white/15 px-4 py-3.5 text-[#FFFFF0] placeholder-[#737373] text-sm"
                  placeholder="+34 600 000 000"
                />
              </Field>

              <Field label="Email *" testid="form-email" full>
                <input
                  type="email"
                  value={form.email}
                  onChange={handle("email")}
                  required
                  data-testid="form-email-input"
                  className="w-full bg-transparent border border-white/15 px-4 py-3.5 text-[#FFFFF0] placeholder-[#737373] text-sm"
                  placeholder="tu@email.com"
                />
              </Field>

              <Field label="Tipo de vehículo *" testid="form-vehicle">
                <select
                  value={form.vehicle_type}
                  onChange={handle("vehicle_type")}
                  required
                  data-testid="form-vehicle-input"
                  className="w-full bg-[#141414] border border-white/15 px-4 py-3.5 text-[#FFFFF0] text-sm cursor-pointer"
                >
                  <option value="">Selecciona una opción</option>
                  {vehicleTypes.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </Field>

              <Field label="Presupuesto aproximado *" testid="form-budget">
                <select
                  value={form.budget}
                  onChange={handle("budget")}
                  required
                  data-testid="form-budget-input"
                  className="w-full bg-[#141414] border border-white/15 px-4 py-3.5 text-[#FFFFF0] text-sm cursor-pointer"
                >
                  <option value="">Selecciona un rango</option>
                  {budgets.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </Field>

              <Field label="Mensaje" testid="form-message" full>
                <textarea
                  value={form.message}
                  onChange={handle("message")}
                  rows={4}
                  data-testid="form-message-input"
                  className="w-full bg-transparent border border-white/15 px-4 py-3.5 text-[#FFFFF0] placeholder-[#737373] text-sm resize-none"
                  placeholder="Cuéntanos más sobre el vehículo que buscas..."
                />
              </Field>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="form-submit-button"
              className="mt-8 w-full sm:w-auto group inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black px-10 py-4 text-sm uppercase tracking-[0.2em] font-medium hover:bg-[#E5C158] transition-all duration-300 disabled:opacity-60"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</>
              ) : (
                <>Enviar solicitud <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} /></>
              )}
            </button>
            <p className="mt-4 text-xs text-[#737373]">
              Al enviar este formulario aceptas que Elecars te contacte para asesorarte sobre tu importación.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, children, full, testid }) => (
  <div className={`${full ? "sm:col-span-2" : ""}`} data-testid={testid}>
    <label className="block text-xs uppercase tracking-[0.2em] text-[#737373] mb-2.5">{label}</label>
    {children}
  </div>
);

export default Contact;
